import React, { Fragment, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import config from '../../../config';

import ReportShell from '../../Admin/Reports/shared/ReportShell';
import ReportFilterBar from '../../Admin/Reports/shared/ReportFilterBar';
import ReportTable from '../../Admin/Reports/shared/ReportTable';
import ReportPagination from '../../Admin/Reports/shared/ReportPagination';
import ReportExportButtons from '../../Admin/Reports/shared/ReportExportButtons';
import useReportLookups from '../../Admin/Reports/shared/useReportLookups';
import {
    buildDynamicField, REPORT_ENDPOINTS, getReportColumns,
    flattenPartnerRows, toExportColumns,
} from '../../Admin/Reports/shared/dynamicReportConfig';

const PAGE_SIZE = 50;
const FB_REPORT = 'F&B Sales Summary Report';
const safeName = (s) => (s || 'DynamicReport').replace(/[^\w.-]+/g, '_');

const RunReport = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const location = useLocation();
    const dynamicReportId = new URLSearchParams(location.search).get('dynamic_report_id');

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [reportName, setReportName] = useState('');
    const [dynamicReportName, setDynamicReportName] = useState('');
    const [reportFilters, setReportFilters] = useState([]);
    const [filterValues, setFilterValues] = useState({});
    const [deliveryPartners, setDeliveryPartners] = useState([]);

    const [reportData, setReportData] = useState([]);
    const [hasRun, setHasRun] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState({ key: '', dir: 'asc' });

    const lookups = useReportLookups({
        propertyId: filterValues.Property,
        categoryId: filterValues.Category,
        subcategoryId: filterValues.Subcategory,
        groupName: filterValues.Group,
    });
    const { concessionaireId, propertyAccessNames } = lookups;

    // Load the saved report definition (base report + its filters/values).
    useEffect(() => {
        axios.post(`${config.hostname}/dynamicreports/getdynamicreport`, { report_id: id, dynamic_report_id: dynamicReportId }, { headers })
            .then((res) => {
                setReportName(res.data.report.report_name);
                setDynamicReportName(res.data.report.dynamic_report_name);
                setReportFilters(res.data.filters || []);
                const init = {};
                (res.data.filters || []).forEach((f) => { init[f.filter_name] = f.filter_value; });
                setFilterValues(init);
            })
            .catch((err) => {
                console.error('load dynamic report failed', err);
                setError(t('Could not load this report definition.'));
            });
    }, [id, dynamicReportId, headers, t]);

    // Delivery partners → dynamic F&B columns.
    useEffect(() => {
        if (!concessionaireId || reportName !== FB_REPORT) return;
        let cancelled = false;
        axios.post(`${config.hostname}/concessionairedeliverypartner/getdeliverypartner`, { concessionaire_id: concessionaireId }, { headers })
            .then((res) => {
                if (cancelled) return;
                setDeliveryPartners((Array.isArray(res.data) ? res.data : [])
                    .map((d) => d.delivery_partner_id?.delivery_partner_name)
                    .filter((n) => n && n !== 'Dine In'));
            })
            .catch((err) => console.warn('delivery partner lookup failed', err?.response?.status));
        return () => { cancelled = true; };
    }, [concessionaireId, reportName, headers]);

    const fields = useMemo(
        () => reportFilters.map((f) => buildDynamicField(f.filter_name, f.filter_name, t, lookups)),
        [reportFilters, t, lookups],
    );

    const columns = useMemo(
        () => getReportColumns(reportName, t, deliveryPartners),
        [reportName, t, deliveryPartners],
    );

    const onFilterChange = (key, value) => {
        setFilterValues((prev) => ({ ...prev, [key]: value }));
        setReportData([]);
        setHasRun(false);
        setPage(0);
    };

    const resetFilters = () => {
        const cleared = {};
        reportFilters.forEach((f) => { cleared[f.filter_name] = ''; });
        setFilterValues(cleared);
        setReportData([]);
        setHasRun(false);
        setError('');
        setPage(0);
        setSort({ key: '', dir: 'asc' });
    };

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, {
            created_by: login_id, concessionaire_id: concessionaireId,
            module: 'Dynamic Report', task,
        }, { headers }).catch((err) => console.warn('audit log failed', err?.response?.status));
    };

    const nameOf = (opts, val) => opts.find((o) => o.value === val)?.label || '';

    const generateReport = async () => {
        const endpoint = REPORT_ENDPOINTS[reportName];
        if (!endpoint) { toast.error(t('Unknown report type')); return; }

        // Date / period filters are mandatory for the underlying reports.
        const REQUIRED = ['Year', 'Start Date', 'End Date', 'Start Month', 'End Month'];
        const missing = reportFilters.some(
            (f) => REQUIRED.includes(f.filter_name) && !filterValues[f.filter_name],
        );
        if (missing) { setError(t('Please fill in all required filters before generating.')); return; }

        const sm = filterValues['Start Month'];
        const em = filterValues['End Month'];
        if (sm && em && parseInt(em, 10) < parseInt(sm, 10)) {
            setError(t('End month cannot be earlier than start month.'));
            return;
        }

        setLoading(true);
        setError('');
        setPage(0);
        const payload = {
            concessionaire_id: concessionaireId,
            property_access: propertyAccessNames,
            year: filterValues.Year || '',
            start_month: sm || '',
            end_month: em || '',
            start_date: filterValues['Start Date'] || '',
            end_date: filterValues['End Date'] || '',
            property_name: nameOf(lookups.propertyOptions, filterValues.Property),
            category_name: nameOf(lookups.categoryOptions, filterValues.Category),
            subcategory_name: nameOf(lookups.subcategoryOptions, filterValues.Subcategory),
            group_name: filterValues.Group || '',
            tenant_name: nameOf(lookups.tenantOptions, filterValues.Tenant),
            floor: filterValues.Floor || '',
        };
        try {
            const res = await axios.post(`${config.hostname}/reports/${endpoint}`, payload, { headers });
            let rows = Array.isArray(res.data) ? res.data : [];
            if (reportName === FB_REPORT) rows = flattenPartnerRows(rows, deliveryPartners);
            setReportData(rows);
            setHasRun(true);
            insertToLog(`Generated ${dynamicReportName}`);
        } catch (err) {
            console.error('run dynamic report failed', err);
            setError(err?.response?.data?.message || t('The report request failed. Please adjust your filters and try again.'));
            toast.error(t('Failed to generate report'));
        } finally {
            setLoading(false);
        }
    };

    const sortedData = useMemo(() => {
        const data = [...reportData];
        if (!sort.key) return data;
        data.sort((a, b) => {
            const av = a[sort.key]; const bv = b[sort.key];
            if (av < bv) return sort.dir === 'asc' ? -1 : 1;
            if (av > bv) return sort.dir === 'asc' ? 1 : -1;
            return 0;
        });
        return data;
    }, [reportData, sort]);

    const pageCount = Math.ceil(sortedData.length / PAGE_SIZE);
    const pageRows = sortedData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    const requestSort = (key) => setSort((p) => ({ key, dir: p.key === key && p.dir === 'asc' ? 'desc' : 'asc' }));

    const exportMetadata = useMemo(() => {
        const human = (name, val) => {
            if (!val) return t('All');
            if (name === 'Property') return nameOf(lookups.propertyOptions, val) || val;
            if (name === 'Category') return nameOf(lookups.categoryOptions, val) || val;
            if (name === 'Subcategory') return nameOf(lookups.subcategoryOptions, val) || val;
            if (name === 'Tenant') return nameOf(lookups.tenantOptions, val) || val;
            return val;
        };
        return [
            ...reportFilters.map((f) => [t(f.filter_name), human(f.filter_name, filterValues[f.filter_name])]),
            [t('Exported By'), login_name],
            [t('Exported On'), new Date().toLocaleString()],
        ];
    }, [reportFilters, filterValues, lookups, login_name, t]);

    return (
        <Fragment>
            <ReportShell
                crumb="Dynamic Reports"
                title={dynamicReportName || t('Dynamic Report')}
                subtitle={reportName ? t('Saved {{base}} — set filters and generate.', { base: t(reportName) }) : ''}
                actions={
                    <ReportExportButtons
                        heading={dynamicReportName || reportName}
                        metadata={exportMetadata}
                        columns={toExportColumns(columns)}
                        data={sortedData}
                        fileName={safeName(dynamicReportName)}
                        landscape
                        onAfterExport={(fmt) => insertToLog(`exported ${dynamicReportName} to ${fmt}`)}
                    />
                }
            >
                {fields.length > 0 && (
                    <ReportFilterBar
                        fields={fields}
                        values={filterValues}
                        onChange={onFilterChange}
                        onGenerate={generateReport}
                        onReset={resetFilters}
                        loading={loading}
                    />
                )}

                <ReportTable
                    columns={columns}
                    rows={pageRows}
                    sort={sort}
                    onSort={requestSort}
                    loading={loading}
                    error={error}
                    emptyMessage={hasRun
                        ? t('No data found for the selected filters.')
                        : t('Choose your filters and select Generate report to view results.')}
                />

                <ReportPagination
                    page={page}
                    pageCount={pageCount}
                    onChange={setPage}
                    totalItems={sortedData.length}
                    pageSize={PAGE_SIZE}
                    itemLabel={t('rows')}
                />
            </ReportShell>
        </Fragment>
    );
};

export default RunReport;
